/**
 * Scroll Reveal Module
 * Animuje elementy wiersz po wierszu - kolejny wiersz pokazuje się
 * dopiero gdy poprzedni jest w 100% widoczny w viewport.
 *
 * Gridy oznaczone atrybutem [data-reveal-grid] — animacja kaskadowa wiersz po wierszu.
 * Elementy .js-reveal poza gridem — animacja prostym IntersectionObserver.
 * MutationObserver automatycznie wykrywa nowo dodane karty.
 */

(function () {
  'use strict';

  // Selektor uniwersalny dla gridów z kaskadową animacją
  var GRID_SELECTOR = '[data-reveal-grid]';

  // Delay między kaflami w wierszu (ms)
  var staggerDelay = 120;

  // Mapa gridów z ich wierszami
  var gridRows = new Map();

  // Tolerancja dla grupowania w wiersze (px)
  var rowTolerance = 10;

  // IntersectionObserver dla elementów poza gridem
  var nonGridObserver = null;

  /**
   * Grupuje karty w wiersze na podstawie ich pozycji top
   */
  function groupCardsIntoRows(grid) {
    var cards = Array.from(grid.querySelectorAll('.js-reveal'));
    var rows = [];

    cards.forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var cardTop = rect.top + window.scrollY; // Pozycja absolutna

      // Znajdź istniejący wiersz lub utwórz nowy
      var foundRow = rows.find(function (row) {
        return Math.abs(row.top - cardTop) < rowTolerance;
      });

      if (foundRow) {
        foundRow.cards.push(card);
      } else {
        rows.push({
          top: cardTop,
          cards: [card],
          isRevealed: false,
          isFullyVisible: false
        });
      }
    });

    // Sortuj wiersze od góry do dołu
    rows.sort(function (a, b) {
      return a.top - b.top;
    });

    // Sortuj karty w każdym wierszu od lewej do prawej
    rows.forEach(function (row) {
      row.cards.sort(function (a, b) {
        return a.getBoundingClientRect().left - b.getBoundingClientRect().left;
      });
    });

    return rows;
  }

  /**
   * Sprawdza czy wiersz jest wystarczająco widoczny w viewport.
   * Na mobile 100% widoczność jest nieosiągalna (karty za duże + translateY),
   * dlatego akceptujemy ~60% widoczności wiersza.
   */
  function isRowFullyVisible(row) {
    var viewportTop = window.scrollY;
    var viewportBottom = viewportTop + window.innerHeight;

    // Znajdź granice wiersza
    var rowTop = Infinity;
    var rowBottom = 0;

    row.cards.forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var cardTop = rect.top + window.scrollY;
      var cardBottom = cardTop + rect.height;

      if (cardTop < rowTop) rowTop = cardTop;
      if (cardBottom > rowBottom) rowBottom = cardBottom;
    });

    // Zapisz granice wiersza do późniejszego użycia
    row.calculatedTop = rowTop;
    row.calculatedBottom = rowBottom;

    var rowHeight = rowBottom - rowTop;
    if (rowHeight <= 0) return false;

    // Oblicz ile procent wiersza jest widoczne w viewport
    var visibleTop = Math.max(rowTop, viewportTop);
    var visibleBottom = Math.min(rowBottom, viewportBottom);
    var visibleHeight = Math.max(0, visibleBottom - visibleTop);
    var visibleRatio = visibleHeight / rowHeight;

    // Na mobile akceptujemy 60% widoczności (desktop: karty mniejsze, łatwo 100%)
    return visibleRatio >= 0.6;
  }

  /**
   * Sprawdza czy wiersz zaczął być widoczny (przynajmniej częściowo)
   */
  function isRowPartiallyVisible(row) {
    var viewportBottom = window.scrollY + window.innerHeight;

    // Znajdź górną krawędź wiersza
    var rowTop = Infinity;

    row.cards.forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var cardTop = rect.top + window.scrollY;
      if (cardTop < rowTop) rowTop = cardTop;
    });

    // Wiersz jest częściowo widoczny gdy jego górna krawędź weszła do viewport
    return rowTop < viewportBottom;
  }

  /**
   * Animuje karty w wierszu z efektem kaskady
   */
  function animateRow(row, grid) {
    if (row.isRevealed) return;

    row.isRevealed = true;
    row.revealedAt = Date.now();

    var totalAnimationTime = row.cards.length * staggerDelay + 50;

    row.cards.forEach(function (card, index) {
      setTimeout(function () {
        card.classList.add('is-revealed');

        // Dla kart z animowanymi elementami - dodaj klasę po zakończeniu animacji
        if (card.classList.contains('feature-card--packshot-title') ||
            card.classList.contains('feature-card--info')) {
          setTimeout(function () {
            card.classList.add('is-animation-done');
          }, 1500);
        }

        // Dla karty welcome - dodaj klasę po zakończeniu animacji
        if (card.classList.contains('feature-card--welcome')) {
          setTimeout(function () {
            card.classList.add('is-animation-done');
          }, 1500);
        }
      }, index * staggerDelay);
    });

    // Po zakończeniu animacji wiersza, sprawdź czy można pokazać kolejny
    // (jeśli następny wiersz jest już w pełni widoczny w viewport)
    setTimeout(function () {
      // Sprawdź czy ten wiersz jest teraz w pełni widoczny
      if (isRowFullyVisible(row)) {
        row.isFullyVisible = true;
      }
      
      if (grid) {
        checkAndAnimateRows(grid);
      }
    }, totalAnimationTime);

    // Safety fallback: jeśli po 1.5s wiersz nadal nie jest oznaczony
    // jako widoczny, wymuś to — zapobiega deadlockowi kaskady na mobile
    setTimeout(function () {
      if (!row.isFullyVisible) {
        row.isFullyVisible = true;
        if (grid) {
          checkAndAnimateRows(grid);
        }
      }
    }, 1500);
  }

  /**
   * Sprawdza i animuje wiersze dla danego grida
   */
  function checkAndAnimateRows(grid) {
    var rows = gridRows.get(grid);
    if (!rows) return;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var prevRow = i > 0 ? rows[i - 1] : null;

      // Pomiń już odkryte wiersze
      if (row.isRevealed) continue;

      // Pierwszy wiersz - animuj gdy zacznie być widoczny
      if (i === 0 && isRowPartiallyVisible(row)) {
        animateRow(row, grid);
        return; // Animuj tylko jeden wiersz na raz
      }

      // Kolejne wiersze - animuj gdy:
      // 1. Poprzedni wiersz jest odkryty I wystarczająco widoczny
      // 2. TEN wiersz jest przynajmniej częściowo widoczny w viewport
      //    (zapobiega kaskadowemu odsłanianiu off-screen przez safety timeout)
      if (prevRow && prevRow.isRevealed) {
        if (prevRow.isFullyVisible || isRowFullyVisible(prevRow)) {
          prevRow.isFullyVisible = true;
          // Animuj TYLKO jeśli wiersz jest widoczny — nie odsłaniaj off-screen
          if (isRowPartiallyVisible(row)) {
            animateRow(row, grid);
            return; // Animuj tylko jeden wiersz na raz
          }
        }
      }
    }
  }

  /**
   * Handler dla scroll i resize
   */
  function handleScrollResize() {
    gridRows.forEach(function (rows, grid) {
      checkAndAnimateRows(grid);
    });
  }

  // Throttle dla scroll
  var ticking = false;
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        handleScrollResize();
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * Tworzy lub zwraca IntersectionObserver dla elementów poza gridem
   */
  function getNonGridObserver() {
    if (nonGridObserver) return nonGridObserver;

    nonGridObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    });

    return nonGridObserver;
  }

  /**
   * Inicjalizacja dla elementów poza gridem
   */
  function initNonGridElements() {
    var allRevealElements = document.querySelectorAll('.js-reveal');
    var observer = getNonGridObserver();

    Array.from(allRevealElements).forEach(function (el) {
      // Pomiń elementy, które są wewnątrz grida
      if (el.closest(GRID_SELECTOR)) return;
      // Pomiń już zarejestrowane elementy
      if (el.hasAttribute('data-reveal-observed')) return;
      el.setAttribute('data-reveal-observed', '');
      observer.observe(el);
    });
  }

  /**
   * Inicjalizuje lub odświeża grid - grupuje karty w wiersze
   */
  function initGrid(grid) {
    var rows = groupCardsIntoRows(grid);
    gridRows.set(grid, rows);
  }

  /**
   * Skanuje DOM i inicjalizuje wszystkie gridy z [data-reveal-grid]
   */
  function discoverGrids() {
    var grids = document.querySelectorAll(GRID_SELECTOR);
    grids.forEach(function (grid) {
      initGrid(grid);
    });
  }

  /**
   * MutationObserver — automatycznie wykrywa nowo dodane karty .js-reveal
   * oraz nowe kontenery [data-reveal-grid].
   * Po dodaniu kart do istniejącego grida przelicza wiersze.
   */
  function initMutationObserver() {
    var mutationObserver = new MutationObserver(function (mutations) {
      var needsGridRefresh = false;
      var needsNonGridRefresh = false;

      mutations.forEach(function (mutation) {
        if (mutation.type !== 'childList' || !mutation.addedNodes.length) return;

        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // Pomiń węzły tekstowe

          // Nowy kontener grida
          if (node.matches && node.matches(GRID_SELECTOR)) {
            needsGridRefresh = true;
          }

          // Nowa karta .js-reveal wewnątrz grida
          if (node.matches && node.matches('.js-reveal') && node.closest(GRID_SELECTOR)) {
            needsGridRefresh = true;
          }

          // Nowa karta .js-reveal poza gridem
          if (node.matches && node.matches('.js-reveal') && !node.closest(GRID_SELECTOR)) {
            needsNonGridRefresh = true;
          }

          // Sprawdź też potomków dodanego node'a
          if (node.querySelectorAll) {
            if (node.querySelectorAll(GRID_SELECTOR).length) {
              needsGridRefresh = true;
            }
            if (node.querySelectorAll('.js-reveal').length) {
              needsGridRefresh = true;
              needsNonGridRefresh = true;
            }
          }
        });
      });

      if (needsGridRefresh) {
        discoverGrids();
        handleScrollResize();
      }
      if (needsNonGridRefresh) {
        initNonGridElements();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Inicjalizacja główna
   */
  /**
   * IntersectionObserver jako backup trigger dla kaskady.
   * Na iOS Safari scroll event nie odpala podczas momentum scrolling.
   * IO odpala niezależnie od eventów JS — ratuje sytuację na iOS.
   */
  function initGridIntersectionBackup() {
    if (!('IntersectionObserver' in window)) return;

    // Obserwujemy każdą kartę w gridzie — przy wejściu/wyjściu z viewport
    // triggerujemy checkAndAnimateRows
    var backupObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var grid = entry.target.closest(GRID_SELECTOR);
        if (grid) {
          checkAndAnimateRows(grid);
        }
      });
    }, {
      root: null,
      rootMargin: '100px 0px',
      threshold: [0, 0.25, 0.5, 0.75]
    });

    var grids = document.querySelectorAll(GRID_SELECTOR);
    grids.forEach(function (grid) {
      var cards = grid.querySelectorAll('.js-reveal');
      cards.forEach(function (card) {
        backupObserver.observe(card);
      });
    });
  }

  function init() {
    // Znajdź i zainicjalizuj wszystkie gridy
    discoverGrids();

    // Dodaj event listenery
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      // Przy resize przelicz wiersze
      discoverGrids();
      handleScrollResize();
    });

    // touchmove jako dodatkowy trigger na iOS
    // (scroll event nie odpala podczas momentum scrolling)
    window.addEventListener('touchmove', onScroll, { passive: true });

    // Inicjalizuj elementy poza gridem
    initNonGridElements();

    // IntersectionObserver backup — najważniejszy fix dla iOS
    initGridIntersectionBackup();

    // Obserwuj DOM pod kątem nowo dodanych kart
    initMutationObserver();

    // Sprawdź początkowy stan (elementy już widoczne przy ładowaniu)
    handleScrollResize();
  }

  // Inicjalizacja po załadowaniu DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
